import React, { useState } from 'react';
import {
    Box,
    Typography,
    Paper,
    List,
    ListItem,
    Stack,
    Chip,
    Button,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Divider,
    Alert,
    Avatar,
    IconButton,
    CircularProgress,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import AddIcon from '@mui/icons-material/Add';
import RestauLocationMappingDialog from './RestauLocationMappingDialog.js';
import { useStyles } from '../home/styles/useStyles';
import Config from '../config/Config.js';
import axios from 'axios';

export default function ExistingIntegrations({
    existingRestauPosMapping,
    setExistingRestauPosMapping,
    restauIdsWithAccess,
    loading,
    setLoading,
    restauId,
}) {
    const classes = useStyles();
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [editingMapping, setEditingMapping] = useState(null);
    const [deletingMapping, setDeletingMapping] = useState(false);

    const handleEditAllMappings = (
        merchantId,
        merchantData,
        posType,
        mappings
    ) => {
        setEditingMapping({
            merchantId,
            merchantData,
            posType,
            isEditing: true,
            existingMappings: mappings, // Pass all current mappings
        });
        setEditDialogOpen(true);
    };

    const handleAddMapping = (merchantId, merchantData, posType) => {
        setEditingMapping({
            merchantId,
            merchantData,
            posType,
            isEditing: false, // This indicates we're adding, not editing
        });
        setEditDialogOpen(true);
    };

    const handleDeleteMapping = async (
        restauId,
        locationId,
        merchantId,
        posType,
        mappings
    ) => {
        if (
            !window.confirm(
                `Are you sure you want to delete the mapping for ${restauIdsWithAccess[restauId]}?`
            )
        ) {
            return;
        }

        //debugger;
        // TODO: Delete from Firebase
        setDeletingMapping(true);
        let deletedMappings = [];
        console.log('Delete mapping:', {
            restauId,
            merchantId,
            posType,
            mappings,
        });
        const mappingExists = mappings.find(
            (m) => m.restauId === restauId && m.locationId === locationId
        );
        if (mappingExists) {
            deletedMappings.push({
                restauId: restauId,
                locationId: locationId,
            });

            await saveRestauIdLocationMappingToDb(
                restauId,
                merchantId,
                [],
                deletedMappings,
                'Mapping deleted successfully!'
            );
        }
        setDeletingMapping(false);
        // Reload the data in background after dialog closes
        reloadPosSettings();
    };

    const saveRestauIdLocationMappingToDb = async (
        restauId,
        merchantId,
        mappings,
        deletedMappings,
        successMessage
    ) => {
        //debugger;
        // Call backend to exchange code for token and get locations
        const response = await axios.post(
            Config.savePosSettings,
            {
                restauId: restauId,
                posMerchantId: merchantId,
                //always contains all the mappings after add/delete
                activeMappings: mappings.reduce((acc, curr) => {
                    acc[curr.restauId.id] = curr.location.id;
                    return acc;
                }, {}),
                //always contains all the mappings which are deleted
                deletedMappings: deletedMappings.reduce((acc, curr) => {
                    acc[curr.restauId] = curr.locationId;
                    return acc;
                }, {}),
            },
            {
                headers: {
                    token: await window.customerApp
                        .auth()
                        .currentUser.getIdToken(),
                },
            }
        );

        console.log('Save mappings response: ', response);

        if (
            response &&
            response.status === 200 &&
            response.data.status === 'success'
        ) {
            alert(successMessage);
        } else {
            alert('Failed to save mappings. Please try again.');
        }
    };

    const handleSaveMappings = async (changeData) => {
        const {
            allMappings,
            addedMappings,
            deletedMappings,
            unchangedMappings,
            merchantId,
        } = changeData;
        const isEditing = editingMapping?.isEditing;

        console.log('Mapping changes:', {
            added: addedMappings,
            deleted: deletedMappings,
            unchanged: unchangedMappings,
            all: allMappings,
        });

        if (isEditing) {
            // For editing mode, send all mappings (API will handle the full update)
            console.log('Updating mappings for merchant:', merchantId);
            console.log(
                'Added:',
                addedMappings.length,
                'Deleted:',
                deletedMappings.length
            );

            await saveRestauIdLocationMappingToDb(
                restauId,
                merchantId,
                allMappings,
                deletedMappings,
                'Mappings updated successfully!'
            );
        } else {
            // For adding new mappings
            console.log('Adding new mappings:', allMappings);
            await saveRestauIdLocationMappingToDb(
                restauId,
                merchantId,
                allMappings,
                [], // No deletions when adding new
                `Successfully added ${allMappings.length} new mapping(s)!`
            );
        }

        // Close dialog first (this will stop showing the loading state)
        setEditDialogOpen(false);
        setEditingMapping(null);

        // Reload the data in background after dialog closes
        reloadPosSettings();
    };

    const reloadPosSettings = async () => {
        try {
            setLoading(true);
            const token = await window.customerApp
                .auth()
                .currentUser.getIdToken();

            const response = await axios.post(
                Config.getPosSettings,
                {
                    restauId: restauId,
                },
                {
                    headers: {
                        token: token,
                    },
                }
            );

            if (response.data && response.data.status) {
                if (response.data.data.posIntegrationsData) {
                    setExistingRestauPosMapping(
                        response.data.data.posIntegrationsData
                    );
                }
            }
        } catch (error) {
            console.error('Error reloading POS settings:', error);
        }
        setLoading(false);
        setDeletingMapping(false);
    };

    // const groupByMerchant = (mappings) => {
    //     const grouped = {};
    //     mappings.forEach((mapping) => {
    //         if (!grouped[mapping.merchantId]) {
    //             grouped[mapping.merchantId] = [];
    //         }
    //         grouped[mapping.merchantId].push(mapping);
    //     });
    //     return grouped;
    // };

    const getPosIcon = (posType) => {
        const icons = {
            square: 'https://storage.googleapis.com/sliceq-takeout/Square-logo-square.webp',
            clover: 'https://storage.googleapis.com/sliceq-takeout/Clover.png',
        };
        return icons[posType] || '';
    };

    const renderMappingsList = () => {
        if (
            !existingRestauPosMapping ||
            Object.keys(existingRestauPosMapping).length === 0
        ) {
            return (
                <Alert severity="info">
                    No existing integrations found. Create a new integration
                    above to get started.
                </Alert>
            );
        }

        return Object.entries(existingRestauPosMapping).map(
            ([merchantId, merchantData]) => {
                const posType =
                    merchantData.provider?.toLowerCase() || 'square';
                const color = posType === 'square' ? 'primary' : 'success';

                // Convert restauMappings array to a flat array of mappings
                const mappings = merchantData.restauMappings.flatMap(
                    (mappingObj) =>
                        Object.entries(mappingObj).map(
                            ([restauId, locationId]) => ({
                                restauId,
                                locationId,
                                locationName:
                                    merchantData.posLocations.find(
                                        (loc) => loc.id === locationId
                                    )?.name || locationId,
                                locationAddress:
                                    merchantData.posLocations.find(
                                        (loc) => loc.id === locationId
                                    )?.address || '',
                            })
                        )
                );

                return (
                    <Accordion key={merchantId} defaultExpanded>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Stack
                                direction={{ xs: 'column', sm: 'row' }}
                                alignItems={{ xs: 'flex-start', sm: 'center' }}
                                spacing={1}
                                sx={{ width: '100%', pr: 2 }}
                            >
                                <Stack
                                    direction="row"
                                    alignItems="center"
                                    spacing={1}
                                >
                                    <Avatar
                                        src={getPosIcon(posType)}
                                        alt={posType}
                                        sx={{ width: 24, height: 24 }}
                                    />
                                    <Typography
                                        variant="subtitle1"
                                        sx={{
                                            fontWeight: 'bold',
                                            wordBreak: 'break-word',
                                            maxWidth: '100%',
                                        }}
                                    >
                                        {posType.charAt(0).toUpperCase() +
                                            posType.slice(1)}{' '}
                                        - {merchantId}
                                    </Typography>
                                </Stack>
                                <Chip
                                    label={`${mappings.length} mapping${
                                        mappings.length !== 1 ? 's' : ''
                                    }`}
                                    size="small"
                                    color={color}
                                />
                            </Stack>
                        </AccordionSummary>
                        <AccordionDetails>
                            <List>
                                {mappings.map((mapping, index) => (
                                    <React.Fragment key={mapping.restauId}>
                                        {index > 0 && (
                                            <Divider sx={{ my: 1 }} />
                                        )}
                                        <ListItem
                                            sx={{
                                                display: 'flex',
                                                flexDirection: {
                                                    xs: 'column',
                                                    md: 'row',
                                                },
                                                justifyContent: 'space-between',
                                                alignItems: {
                                                    xs: 'stretch',
                                                    md: 'center',
                                                },
                                                px: { xs: 1, sm: 2 },
                                                py: 1.5,
                                                gap: { xs: 2, md: 0 },
                                            }}
                                        >
                                            <Stack
                                                direction={{
                                                    xs: 'column',
                                                    sm: 'row',
                                                }}
                                                spacing={{ xs: 1, sm: 2 }}
                                                alignItems={{
                                                    xs: 'flex-start',
                                                    sm: 'center',
                                                }}
                                                justifyContent="center"
                                                sx={{ flex: 1, minWidth: 0 }}
                                            >
                                                <Box
                                                    sx={{
                                                        flex: 0.7,
                                                        minWidth: 0,
                                                        display: 'flex',
                                                        justifyContent:
                                                            'center',
                                                    }}
                                                >
                                                    <Stack
                                                        direction="row"
                                                        spacing={1}
                                                        alignItems="center"
                                                    >
                                                        <RestaurantIcon fontSize="small" />
                                                        <Box
                                                            sx={{
                                                                textAlign:
                                                                    'center',
                                                            }}
                                                        >
                                                            <Typography
                                                                variant="body2"
                                                                sx={{
                                                                    fontWeight:
                                                                        'bold',
                                                                    wordBreak:
                                                                        'break-word',
                                                                }}
                                                            >
                                                                {restauIdsWithAccess[
                                                                    mapping
                                                                        .restauId
                                                                ] ||
                                                                    mapping.restauId}
                                                            </Typography>
                                                            <Typography
                                                                variant="caption"
                                                                color="text.secondary"
                                                                sx={{
                                                                    display:
                                                                        'block',
                                                                }}
                                                            >
                                                                {
                                                                    mapping.restauId
                                                                }
                                                            </Typography>
                                                        </Box>
                                                    </Stack>
                                                </Box>
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                    sx={{
                                                        display: {
                                                            xs: 'none',
                                                            sm: 'block',
                                                        },
                                                    }}
                                                >
                                                    →
                                                </Typography>
                                                <Box
                                                    sx={{
                                                        flex: 1.5,
                                                        minWidth: 0,
                                                        display: 'flex',
                                                        justifyContent:
                                                            'center',
                                                    }}
                                                >
                                                    <Stack
                                                        direction="row"
                                                        spacing={1}
                                                        alignItems="center"
                                                    >
                                                        <LocationOnIcon
                                                            fontSize="small"
                                                            color={color}
                                                        />
                                                        <Box
                                                            sx={{
                                                                minWidth: 0,
                                                                overflow:
                                                                    'hidden',
                                                                textAlign:
                                                                    'center',
                                                            }}
                                                        >
                                                            <Typography
                                                                variant="body2"
                                                                sx={{
                                                                    fontWeight:
                                                                        'bold',
                                                                    wordBreak:
                                                                        'break-word',
                                                                }}
                                                            >
                                                                {
                                                                    mapping.locationName
                                                                }
                                                            </Typography>
                                                            <Typography
                                                                variant="caption"
                                                                color="text.secondary"
                                                                sx={{
                                                                    wordBreak:
                                                                        'break-all',
                                                                    display:
                                                                        'block',
                                                                }}
                                                            >
                                                                {
                                                                    mapping.locationAddress
                                                                }
                                                            </Typography>
                                                        </Box>
                                                    </Stack>
                                                </Box>
                                            </Stack>
                                            <Stack
                                                direction="row"
                                                spacing={1}
                                                sx={{
                                                    flexShrink: 0,
                                                    alignSelf: {
                                                        xs: 'flex-end',
                                                        md: 'center',
                                                    },
                                                }}
                                            >
                                                {deletingMapping ? (
                                                    <CircularProgress
                                                        size={24}
                                                        style={{
                                                            color: 'black',
                                                        }}
                                                    />
                                                ) : (
                                                    <IconButton
                                                        size="small"
                                                        color="error"
                                                        onClick={() =>
                                                            handleDeleteMapping(
                                                                mapping.restauId,
                                                                mapping.locationId,
                                                                merchantId,
                                                                posType,
                                                                mappings
                                                            )
                                                        }
                                                    >
                                                        <DeleteIcon fontSize="small"></DeleteIcon>
                                                    </IconButton>
                                                )}
                                            </Stack>
                                        </ListItem>
                                    </React.Fragment>
                                ))}
                            </List>
                            <Divider sx={{ my: 2 }} />
                            <Box sx={{ px: 2, pb: 1 }}>
                                <Stack direction="row" spacing={2}>
                                    <Button
                                        variant="outlined"
                                        startIcon={<EditIcon />}
                                        onClick={() =>
                                            handleEditAllMappings(
                                                merchantId,
                                                merchantData,
                                                posType,
                                                mappings
                                            )
                                        }
                                        fullWidth
                                        size="small"
                                        color="primary"
                                    >
                                        Edit Mappings
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        startIcon={<AddIcon />}
                                        onClick={() =>
                                            handleAddMapping(
                                                merchantId,
                                                merchantData,
                                                posType
                                            )
                                        }
                                        fullWidth
                                        size="small"
                                    >
                                        Add New Mapping
                                    </Button>
                                </Stack>
                            </Box>
                        </AccordionDetails>
                    </Accordion>
                );
            }
        );
    };

    if (loading) {
        return (
            <Paper sx={{ p: 3, textAlign: 'center' }}>
                <img
                    className={classes.loadingImage}
                    src="../../img/spinner.gif"
                    alt="Loading..."
                />
            </Paper>
        );
    }

    return (
        <Box>
            <Typography variant="h6" gutterBottom>
                Existing Integrations
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Manage your existing restaurant-to-POS location mappings
            </Typography>

            {renderMappingsList()}

            {/* Mapping Dialog - Used for both adding and editing */}
            {editDialogOpen && editingMapping && (
                <RestauLocationMappingDialog
                    open={editDialogOpen}
                    onClose={() => {
                        setEditDialogOpen(false);
                        setEditingMapping(null);
                    }}
                    onSave={handleSaveMappings}
                    posType={editingMapping.posType}
                    posLocations={
                        editingMapping.merchantData?.posLocations || []
                    }
                    restauIdsWithAccess={restauIdsWithAccess}
                    existingRestauPosMappings={existingRestauPosMapping}
                    merchantId={editingMapping.merchantData.merchantId}
                    existingMappings={
                        editingMapping.isEditing &&
                        editingMapping.existingMappings
                            ? editingMapping.existingMappings
                            : null
                    }
                />
            )}
        </Box>
    );
}
